/**
 * @typedef {Object} Plugin:Tracking.DataLayerObjBase
 * @property {Object<string,boolean>} events
 * @property {Plugin:Tracking.PageObj} page
 * @property {Plugin:Tracking.DataLayerUser} user
 * @property {Object} [booking]
 */
/**
 * @typedef {Object} Plugin:Tracking.PageObj
 * @property {string} name - Ex. 'mohg >> home',
 * @property {string} url - Ex. 'http - Ex.//www.mandarinoriental.com/index',
 * @property {string} siteSection - Ex. 'mohg >> home',
 * @property {string} hotelCode - Ex. 'global',
 * @property {string} language - Ex. 'english',
 * @property {string} outlet - Ex. 'responsive',
 * @property {string} template - Ex. 'main/brand-home.xsl'
 */
/**
 * @typedef {Object} Plugin:Tracking.DataLayerUser
 * @property {string} accountNumber - User account number
 * @property {string} accountType - User account type
 * @property {'visitor'|'logged in'} loginState - User login state
 */
import $ from 'jquery'
import cloneDeep from 'lodash.clonedeep'
import { getQueryParamByName } from '@utils/get-query-parameters'
import { logger } from './logger'
/**
 * @module Plugin:Tracking
 * @description
 * Handle data layer tracking.
 * [check guide]{@link /docs/consentandtracking}
 * @example
 * // In component
 * export default {
 *   mounted() {
 *     this.$track(DataLayerObj)
 *   }
 * }
 */
/**
 * Transforms all string keys and values to lower case. (deprecated)
 * This transformation is been done by BE, kept just for archive
 * @param {Plugin:Tracking.DataLayerObjBase} obj - Object to be transformed
 * @returns {Plugin:Tracking.DataLayerObjBase}
 */
const ObjectTransform = (obj) => {
  const transformed = {}
  if (!obj) {
    return
  }
  Object.keys(obj).forEach((key) => {
    const value = obj[key]
    if (typeof value === 'string') {
      transformed[key.toLocaleLowerCase()] = value.toLocaleLowerCase()
    } else if (Array.isArray(value)) {
      const transformedArray = []
      value.forEach((item) => {
        let transformedItem
        if (typeof item === 'string') {
          transformedItem = item.toLocaleLowerCase()
        } else if (typeof item === 'object') {
          transformedItem = ObjectTransform(item)
        } else {
          transformedItem = item
        }
        transformedArray.push(transformedItem)
      })
      transformed[key.toLocaleLowerCase()] = transformedArray
    } else if (typeof value === 'object') {
      transformed[key.toLocaleLowerCase()] = ObjectTransform(value)
    } else {
      transformed[key.toLocaleLowerCase()] = value
    }
  })
  return transformed
}
/**
 * Default variables from trackers
 * @var {Plugin:Tracking.DataLayerObjBase} __initialpageviewdata - Information data coming from sitecore
 * @var {Function} _trackData - Acronym function for tracking
 * @var {Array} gtmLayer - Array of events managed by GTM, push objects to it to track
 * https://developers.google.com/tag-manager/devguide
 * @var {String} defaultTracker - Will be used when only an dataLayer object is provided
 * @var {Array} AvailableTrackers - [gtm: Google Tag Manager, dtm: Adobe Tag Manager]
 * @var {Object} AvailableTrackers[name] - Tracker object
 * @var {Function} AvailableTrackers[name].callback - Function that will be called
 * when this tracker is used
 */
const {
  __initialpageviewdata = cloneDeep(window.DefaultDataLayer || {}),
} = window
if (__initialpageviewdata?.events) {
  delete __initialpageviewdata.events
}

const defaultTrackers = ['dtmV2']
const AvailableTrackers = {
  dtmV2: {
    callback: (dataLayer) => {
      if (!window.adobeDataLayer) return
      // Todo: this should be removed from BE before the final Adobe V2 launch
      // delete the form object
      // delete dataLayer.form

      // Override for eventName, cannot use event name in BE code
      if (dataLayer.eventName) {
        dataLayer.event = dataLayer.eventName
        delete dataLayer.eventName
      }

      if (window.adobeDataLayer) {
        window.adobeDataLayer.push(dataLayer)
        logger.debug('window.adobeDataLayer: ', window.adobeDataLayer)
      } else {
        const interval = setInterval(() => {
          if (window.adobeDataLayer) {
            window.adobeDataLayer.push(dataLayer)
            clearInterval(interval)
          }
        }, 1000)
      }
    }
  },

}
/**
 * Track event that can be configured to track in multiple trackes
 * @param {String|Array|Object} trackers - Trackers to run, if no tracker is given tracks to the
 * defaultTracker
 * @param {Object} trackingParams - Params to track, if `trackers` param is an Object will be skiped
 * @param {Object} trackingParams.events - The events object must be set by the frontend
 * @param {Boolean} trackingParams.events[eventName] - Set each event needed to true
 */
export const track = (trackers, trackingParams) => {
  // If no trackers provided, do nothing
  if (!trackers) {
    return
  }
  let trackersArray
  let dataLayer = trackingParams
  if (Array.isArray(trackersArray)) {
    trackersArray = trackers
  } else if (typeof trackers === 'string') {
    trackersArray = [trackers]
  // If trackers is an object will use it as
  // the dataLayer and track with the defaultTracker
  } else if (typeof trackers === 'object') {
    trackersArray = defaultTrackers
    dataLayer = trackers
  }
  // If no dataLayer do nothing
  if (!dataLayer || $.isEmptyObject(dataLayer)) {
    // return
  }
  // else if (!dataLayer.events || $.isEmptyObject(dataLayer.events)) {
  //   throw new Error('Events property cannot be empty')
  // }
  if (__initialpageviewdata?.page?.url) {
    __initialpageviewdata.page.url = window.location.href

    const hotelCodeFromQuery = getQueryParamByName(window.location.href, 'hotelCode')
    const isReservationPage = window.location.pathname.toLowerCase().includes('/reservation')
    const isSpaSoftPage = window.location.pathname.toLowerCase().includes('/spa-soft-booking') || window.location.pathname.toLowerCase().includes('/spa-reservation') || window.location.pathname.toLowerCase().includes('/spa-reservation-details')

    if ((isReservationPage || isSpaSoftPage) && hotelCodeFromQuery && __initialpageviewdata?.page?.hotelCode) {
      __initialpageviewdata.page.hotelCode = hotelCodeFromQuery
    }
  } else {
    logger.error('DefaultDataLayer not found')
  }
  trackersArray.forEach((t) => {
    if (!AvailableTrackers[t]) {
      throw new Error('Unkonw tracking service')
    } else {
      try {
        AvailableTrackers[t].callback($.extend(true, {}, __initialpageviewdata, dataLayer))
      } catch (err) {
        logger.error('Tracking callback error')
        logger.error(err)
      }
      // AvailableTrackers[t].callback(dataLayer)
    }
  })
}

const install = (Vue) => {
  if (__initialpageviewdata?.page?.name?.includes('error')) {
    // Track error page
    const errorType = __initialpageviewdata.page.name.split('>>')[2].trim()
    track({ events: { pageView: true, error: true }, page: { errors: [{ code: errorType }] } })
  } else {
    // On SBE Modify page with lookup form and detail we restrict the initialDataLayer
    const hasRestrictInitialDataLayer = !!document.querySelector('input[data-restrict-initial-datalayer="true"]')
    // In SBE Modify Booking Edit Mode in Booking Wizard pages we restrict the initialDataLayer
    const pageHref = window.location.href
    const isBookingEditMode = getQueryParamByName(pageHref, 'bookingEditMode') === 'true'
    const isSpaSoftPage = window.location.pathname.toLowerCase().includes('/spa-soft-booking') || window.location.pathname.toLowerCase().includes('/spa-reservation')

    if (hasRestrictInitialDataLayer === false && (!isBookingEditMode || !isSpaSoftPage)) {
      track({})
    }
  }
  // track(__initialpageviewdata)
  Vue.prototype.$track = track
}

export default {
  install
}
